import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Newspaper, ArrowLeft, GitBranch, Radio, FileText, LayoutDashboard, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/jwt-auth-client';
import { cn } from '@/lib/utils';
import { useWorkspaceFromRoute } from "@/hooks/useWorkspaceFromRoute";
import { useBreadcrumbBack } from '@/contexts/BreadcrumbContext';
import { useIsMobile } from "@/hooks/useIsMobile";
import { highlightElement } from '@/shared/lib/ui-highlight';
import { emitContentStudioEvent } from '@/features/content/lib/contentStudioEvents';
import { contentArticlePage, contentSourcePage } from '@/features/department/lib/departmentRoutes';
import { useNovaStore } from '../store/useNovaStore';
import { appEvents } from "@/shared/lib/events";

// Lazy-load tab components — only the active tab's code is downloaded
const OverviewTab = lazy(() => import('./overview/OverviewTab').then(m => ({ default: m.OverviewTab })));
const ArticlesTab = lazy(() => import('./ArticlesTab').then(m => ({ default: m.ArticlesTab })));
const SourcesTab = lazy(() => import('./SourcesTab').then(m => ({ default: m.SourcesTab })));
const FlowsTab = lazy(() => import('./FlowsTab').then(m => ({ default: m.FlowsTab })));

function TabFallback() {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );
}

type Tab = 'overview' | 'flows' | 'sources' | 'articles';
const VALID_TABS: Tab[] = ['overview', 'articles', 'sources', 'flows'];
const DEFAULT_TAB: Tab = 'overview';

interface Workspace {
  id: string;
  name: string;
}

export default function ContentStudioWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaceId: routeWorkspaceId, wsSlug } = useWorkspaceFromRoute();
  const goBack = useBreadcrumbBack(
    wsSlug ? `/workspaces/${wsSlug}/departments/marketing` : "/departments/marketing",
  );
  const workspaceId = searchParams.get("ws") ?? routeWorkspaceId ?? "";
  const isMobile = useIsMobile();

  const navigate = useNavigate();

  // Tab state driven by URL search param
  const rawTab = searchParams.get("tab") as Tab | null;
  const activeTab: Tab = rawTab && VALID_TABS.includes(rawTab) ? rawTab : DEFAULT_TAB;
  const setFocusTab = useNovaStore((s) => s.setFocusTab);

  const setActiveTab = useCallback(
    (tab: Tab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      }, { replace: true });
      setFocusTab(tab);
    },
    [setSearchParams, setFocusTab],
  );

  // Sync active tab to Nova focus state
  useEffect(() => {
    setFocusTab(activeTab);
  }, [activeTab, setFocusTab]);

  // Ensure the tab param is always present in the URL (even on first load)
  useEffect(() => {
    if (!searchParams.has("tab")) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", DEFAULT_TAB);
        return next;
      }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [workspace, setWorkbench] = useState<Workspace | null>(null);
  const [wbLoading, setWbLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    setWbLoading(true);
    apiFetch(`/api/workspaces/${workspaceId}/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setWorkbench(data))
      .catch(() => {})
      .finally(() => setWbLoading(false));

    // Register the config:content-studio artifact for this workspace
    apiFetch("/core/content-studio/setup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspaceId, config: {} }),
    }).catch(() => {});
  }, [workspaceId]);

  // ── Content-studio-ui-action listener (mirrors AdsWorkspace ads-ui-action) ──
  useEffect(() => {
    const handler = (event: { command: string; params: Record<string, unknown> }) => {
      const { command, params } = event;
      switch (command) {
        case "switch_tab": {
          const tab = params.tab as Tab;
          if (tab === "overview" || tab === "flows" || tab === "sources" || tab === "articles") {
            setActiveTab(tab);
          }
          break;
        }
        case "set_filter": {
          setActiveTab("articles");
          emitContentStudioEvent("apply_filters", {
            statusGroup: (params.statusGroup ?? params.status_group) as string | undefined,
            sourceId: (params.sourceId ?? params.source_id) as string | undefined,
            search: params.search as string | undefined,
          });
          break;
        }
        case "highlight": {
          const target = typeof params.target === "string" ? params.target : "";
          if (target) {
            highlightElement({
              target,
              comment: typeof params.comment === "string" ? params.comment : undefined,
              pulses: typeof params.pulses === "number" ? params.pulses : undefined,
            });
          }
          break;
        }
        case "navigate_article": {
          const articleId = (params.articleId ?? params.article_id) as string;
          if (articleId && wsSlug) {
            navigate(contentArticlePage(wsSlug, articleId));
          }
          break;
        }
        case "navigate_source": {
          const sourceId = (params.sourceId ?? params.source_id) as string;
          if (sourceId && wsSlug) {
            navigate(contentSourcePage(wsSlug, sourceId));
          }
          break;
        }
        case "scroll_to_article": {
          setActiveTab("articles");
          const artId = (params.articleId ?? params.article_id) as string;
          if (artId) {
            setTimeout(() => {
              highlightElement({
                target: `content-studio-article-${artId}`,
                comment: typeof params.comment === "string" ? params.comment : undefined,
              });
            }, 350);
          }
          break;
        }
        case "show_source": {
          setActiveTab("sources");
          const srcId = (params.sourceId ?? params.source_id) as string;
          if (srcId) {
            setTimeout(() => {
              highlightElement({
                target: `content-studio-source-${srcId}`,
                comment: typeof params.comment === "string" ? params.comment : undefined,
              });
            }, 350);
          }
          break;
        }
        case "open_source_wizard": {
          setActiveTab("sources");
          setTimeout(() => emitContentStudioEvent("open_source_wizard"), 100);
          break;
        }
        case "open_flow_wizard": {
          setActiveTab("flows");
          setTimeout(() => emitContentStudioEvent("open_flow_wizard"), 100);
          break;
        }
        case "open_flow_detail": {
          setActiveTab("flows");
          const fId = (params.flowId ?? params.flow_id) as string;
          if (fId) setTimeout(() => emitContentStudioEvent("open_flow_detail", { flowId: fId }), 100);
          break;
        }
        case "open_quick_add": {
          setActiveTab("articles");
          setTimeout(() => emitContentStudioEvent("open_quick_add", {
            url: typeof params.url === "string" ? params.url : undefined,
          }), 100);
          break;
        }
        case "show_flow": {
          setActiveTab("flows");
          const flowId = (params.flowId ?? params.flow_id) as string;
          if (flowId) {
            setTimeout(() => {
              highlightElement({
                target: `content-studio-flow-${flowId}`,
                comment: typeof params.comment === "string" ? params.comment : undefined,
              });
            }, 350);
          }
          break;
        }
        case "set_view_mode": {
          setActiveTab("articles");
          const mode = params.mode as string;
          if (mode) setTimeout(() => emitContentStudioEvent("set_view_mode", { viewMode: mode }), 100);
          break;
        }
        case "filter_sources": {
          setActiveTab("sources");
          setTimeout(() => emitContentStudioEvent("filter_sources", {
            typeFilter: typeof params.type === "string" ? params.type : undefined,
            statusFilter: typeof params.status === "string" ? params.status : undefined,
            search: typeof params.search === "string" ? params.search : undefined,
          }), 100);
          break;
        }
      }
    };
    return appEvents.subscribe("content-studio-ui-action", handler);
  }, [workspaceId, wsSlug, navigate, setActiveTab]);

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'articles', label: 'Articles', icon: <FileText className="w-4 h-4" /> },
    { id: 'sources', label: 'Sources', icon: <Radio className="w-4 h-4" /> },
    { id: 'flows', label: 'Flows', icon: <GitBranch className="w-4 h-4" /> },
  ];

  return (
    <div className={cn(
      "h-full w-full bg-background font-sans overflow-hidden",
      isMobile ? "flex flex-col" : "flex",
    )}>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-muted/30 shrink-0">
          <button
            onClick={goBack}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-500/10">
              <Newspaper className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h1 className="ds-body-sm-strong text-foreground">Content Studio</h1>
              {wbLoading ? (
                <p className="ds-caption text-muted-foreground mt-0.5">Loading...</p>
              ) : workspace ? (
                <p className="ds-caption text-muted-foreground mt-0.5">{workspace.name}</p>
              ) : null}
            </div>
          </div>

          {/* Tabs */}
          <div className="ml-auto flex items-center gap-1 bg-muted/50 rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-highlight-id={`content-studio-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "ds-tab-label flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all",
                  activeTab === tab.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<TabFallback />}>
            {activeTab === 'overview' && (
              <OverviewTab
                workspaceId={workspaceId}
                onNavigate={setActiveTab}
                onArticleClick={(id) => wsSlug && navigate(contentArticlePage(wsSlug, id))}
                onSourceClick={(id) => wsSlug && navigate(contentSourcePage(wsSlug, id))}
              />
            )}
            {activeTab === 'flows' && <FlowsTab workspaceId={workspaceId} />}
            {activeTab === "sources" && <SourcesTab workspaceId={workspaceId} />}
            {activeTab === "articles" && <ArticlesTab workspaceId={workspaceId} />}
          </Suspense>
        </div>
      </div>
    </div>
  );
}
